import { motion } from 'framer-motion';
import { Star, Quote } from 'lucide-react';

const reviews = [
  {
    id: 1,
    name: 'Maria Santos',
    review: 'Muito pacientes e cuidadosos com os animais. O meu cão ficou lindo e super relaxado depois do banho!',
    rating: 5,
    pet: 'Max - Labrador',
  },
  {
    id: 2,
    name: 'João Ferreira',
    review: 'Funcionários atenciosos, super recomendo! Tratam os animais com muito amor e carinho.',
    rating: 5,
    pet: 'Luna - Poodle',
  },
  {
    id: 3,
    name: 'Ana Rodrigues',
    review: 'Ambiente calmo e excelente trabalho. A minha gata ficou maravilhosa e muito mais tranquila do que em outros sítios.',
    rating: 5,
    pet: 'Mia - Gata Persa',
  },
  {
    id: 4,
    name: 'Pedro Costa',
    review: 'Profissionalismo de excelência! O meu cão costumava ter medo de tosquias, mas aqui ficou super calmo.',
    rating: 5,
    pet: 'Thor - Golden Retriever',
  },
  {
    id: 5,
    name: 'Sofia Martins',
    review: 'Adorei o atendimento! Muito cuidado com o meu cachorrinho que é muito nervoso. Voltaremos sempre!',
    rating: 5,
    pet: 'Bingo - Yorkshire',
  },
  {
    id: 6,
    name: 'Ricardo Almeida',
    review: 'Excelente qualidade de serviço. O resultado foi incrível e o preço muito justo. Recomendo a todos!',
    rating: 5,
    pet: 'Rocky - Bulldog',
  },
];

const StarRating = ({ rating }) => (
  <div className="flex gap-1">
    {[...Array(5)].map((_, i) => (
      <Star
        key={i}
        className={`w-4 h-4 ${i < rating ? 'text-yellow-500 fill-yellow-500' : 'text-gray-300'}`}
      />
    ))}
  </div>
);

export const Reviews = () => {
  return (
    <section 
      id="avaliacoes" 
      className="py-20 md:py-28 bg-white relative overflow-hidden"
      data-testid="reviews-section"
    >
      {/* Background decoration */}
      <div className="absolute top-1/2 left-0 w-72 h-72 bg-yellow-100/50 rounded-full blur-3xl -z-10 -translate-y-1/2" />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section header */}
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <div className="inline-flex items-center gap-2 bg-yellow-100 text-yellow-800 px-4 py-2 rounded-full mb-6">
            <Star className="w-5 h-5 fill-yellow-500 text-yellow-500" />
            <span className="font-bold text-lg">5,0</span>
            <span className="text-sm">no Google (126 avaliações)</span>
          </div>
          
          <h2 className="font-nunito text-3xl sm:text-4xl lg:text-5xl font-extrabold text-gray-900 mb-4">
            O que dizem os nossos<br />
            <span className="text-yellow-500">clientes</span>
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            A satisfação dos nossos clientes e dos seus patudos é a nossa maior recompensa.
          </p>
        </motion.div>

        {/* Reviews Grid */}
        <motion.div
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={{
            hidden: { opacity: 0 },
            visible: {
              opacity: 1,
              transition: { staggerChildren: 0.1 },
            },
          }}
        >
          {reviews.map((review, index) => (
            <motion.div
              key={review.id}
              className="bg-white rounded-3xl p-6 shadow-xl shadow-black/5 border border-gray-100 hover:border-yellow-300 transition-colors group"
              variants={{
                hidden: { opacity: 0, y: 20 },
                visible: { opacity: 1, y: 0 },
              }}
              whileHover={{ y: -8 }}
              transition={{ duration: 0.3 }}
              data-testid={`review-card-${index}`}
            >
              {/* Quote icon */}
              <div className="w-10 h-10 bg-yellow-100 rounded-xl flex items-center justify-center mb-4 group-hover:bg-yellow-200 transition-colors">
                <Quote className="w-5 h-5 text-yellow-600" />
              </div>

              {/* Rating */}
              <StarRating rating={review.rating} />

              {/* Review text */}
              <p className="text-gray-700 mt-4 mb-6 leading-relaxed">
                "{review.review}"
              </p>

              {/* Author */}
              <div className="flex items-center gap-3 pt-4 border-t border-gray-100">
                <div className="w-10 h-10 bg-yellow-500 rounded-full flex items-center justify-center">
                  <span className="text-gray-900 font-bold text-sm">
                    {review.name.split(' ').map(n => n[0]).join('')}
                  </span>
                </div>
                <div>
                  <p className="font-semibold text-gray-900">{review.name}</p>
                  <p className="text-sm text-gray-500">{review.pet}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* Google badge */}
        <motion.div
          className="mt-12 text-center"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.5 }}
        >
          <a
            href="https://www.google.com/search?q=pet+guardioes+setubal"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-3 text-gray-600 hover:text-yellow-600 transition-colors"
          >
            <svg className="w-6 h-6" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            <span className="font-medium">Ver todas as avaliações no Google</span>
          </a>
        </motion.div>
      </div>
    </section>
  );
};
